import React, { useEffect, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Alert,
  Fade,
  Card,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, Close as CloseIcon } from '@mui/icons-material';
import { ContractFormConfig, FormData, FieldConfig, FieldType } from '../types/contractForm.types';
import { FieldRenderer } from './FieldRenderer';
import { useRAG } from '../hooks/useRAG';
import StepperProgress from './shared/StepperProgress';
import FormStep from './shared/FormStep';
import StepNavigation from './shared/StepNavigation';

/**
 * Props for GenericContractForm component
 */
export interface GenericContractFormProps {
  /** Form configuration */
  config: ContractFormConfig;
  /** Form submission handler */
  onSubmit?: (data: FormData) => Promise<void>;
  /** Success handler (legacy compatibility) - called after successful form submission */
  onSuccess?: (result: any) => void;
  /** Preview handler (optional) */
  onPreview?: (data: FormData) => void;
  /** Auto-save handler (optional) */
  onAutoSave?: (data: FormData) => void;
  /** Initial form data (optional) */
  initialData?: Partial<FormData>;
  /** Existing data for edit mode (legacy compatibility) */
  existingData?: any;
  /** Edit mode flag (legacy compatibility) */
  editMode?: boolean;
  /** Contract ID for editing (optional) */
  contractId?: string;
  /** Render as dialog (default: false) */
  isDialog?: boolean;
  /** Dialog open state (default: true) */
  open?: boolean;
  /** Dialog close handler */
  onClose?: () => void;
}

/**
 * GenericContractForm Component
 *
 * A configuration-driven contract form that supports:
 * - Multi-step navigation with validation
 * - Conditional field rendering
 * - RAG-powered AI suggestions
 * - Auto-save functionality
 * - Field-level and form-level validation
 * - Dialog and standalone modes
 *
 * @component
 * @example
 * ```tsx
 * <GenericContractForm
 *   config={purchaseContractConfig}
 *   onSubmit={handleSubmit}
 *   onPreview={handlePreview}
 *   onAutoSave={handleAutoSave}
 *   isDialog={true}
 *   open={true}
 *   onClose={handleClose}
 * />
 * ```
 */
export const GenericContractForm: React.FC<GenericContractFormProps> = ({
  config,
  onSubmit,
  onSuccess,
  onPreview,
  onAutoSave,
  initialData,
  existingData,
  editMode,
  contractId,
  isDialog = false,
  open = true,
  onClose,
}) => {
  // Legacy compatibility: use existingData if provided
  const effectiveInitialData = existingData || initialData;
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [incompleteSteps, setIncompleteSteps] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // Dynamic tenants for rental contract step "Kdo si pronajme?"
  const [extraTenants, setExtraTenants] = useState<number>(0);
  // Dynamic landlords for rental contract step "Kdo pronajímá?"
  const [extraLandlords, setExtraLandlords] = useState<number>(0);

  // Merge initial values from config and props
  // Also absorb field-level defaultValue from the configuration so RHF starts with stable states (esp. checkboxes)
  const isCheckboxField = useCallback((field: FieldConfig | { type?: string }) => {
    const fieldType = (field as any)?.type;
    if (!fieldType) return false;
    if (typeof fieldType === 'string') {
      return fieldType.toLowerCase() === FieldType.CHECKBOX;
    }
    return fieldType === FieldType.CHECKBOX;
  }, []);

  const steps = React.useMemo(() => {
    return (config.steps || []).map((step) => ({
      ...step,
      fields: step.fields.map((field) =>
        isCheckboxField(field) && field.conditional
          ? { ...field, conditional: undefined }
          : field
      ),
    }));
  }, [config.steps, isCheckboxField]);

  const defaultsFromConfig = React.useMemo(() => {
    const out: Record<string, any> = {};
    for (const step of steps) {
      for (const f of step.fields || []) {
        if ((f as any).defaultValue !== undefined) {
          out[f.name] = (f as any).defaultValue;
        } else if (isCheckboxField(f) && out[f.name] === undefined) {
          // Ensure checkboxes have explicit boolean default
          out[f.name] = false;
        }
      }
    }
    return out;
  }, [steps, isCheckboxField]);

  const defaultValues = {
    ...(config.initialValues || {}),
    ...defaultsFromConfig,
    ...(initialData || {}),
  };

  // Initialize React Hook Form
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    watch,
    formState,
    setValue,
    getValues,
    trigger,
    reset,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
    // Preserve values for fields that unmount (e.g., when switching steps or toggling visibility)
    // This prevents checkbox states and other inputs from resetting or "disappearing" on re-render.
    shouldUnregister: false,
  });

  const watchedData = watch();

  // RAG integration (if enabled)
  const {
    isAvailable: ragAvailable,
    isLoading: ragLoading,
    recommendations,
    enhanceContract,
    clearRecommendations,
  } = useRAG();

  // Build RAG suggestions map from field names
  const ragSuggestions: Record<string, string[]> = {};
  if (config.rag?.enabled && config.rag.fields) {
    config.rag.fields.forEach((fieldName) => {
      ragSuggestions[fieldName] = recommendations;
    });
  }

  /**
   * Auto-save effect
   */
  useEffect(() => {
    if (!onAutoSave || activeStep === 0) return;

    setAutoSaveStatus('saving');
    const timeoutId = setTimeout(() => {
      try {
        onAutoSave(getValues());
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Auto-save failed:', err);
        setAutoSaveStatus('error');
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [watchedData, activeStep, onAutoSave, getValues]);

  /**
   * RAG enhancement effect
   */
  useEffect(() => {
    if (!config.rag?.enabled || !ragAvailable || activeStep === 0) return;

    const timeoutId = setTimeout(() => {
      enhanceContract(config.type as any, getValues());
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [watchedData, config.rag?.enabled, config.type, ragAvailable, enhanceContract, getValues, activeStep]);

  /**
   * Dynamic contract_subtype options update based on contract_type
   */
  useEffect(() => {
    // Only for purchase contracts with dynamic subtypes
    if (config.type !== 'purchase') return;

    const contractType = watchedData['contract_type'];
    if (!contractType) return;

    // Find contract_subtype field in the current config
    const firstStep = steps[0];
    if (!firstStep) return;

    const subtypeField = firstStep.fields.find(f => f.name === 'contract_subtype');
    if (!subtypeField) return;

    // Get subtype options dynamically based on contract_type
    // Import getSubtypeOptions from config
    const getSubtypeOptions = (contractType: string) => {
      const subtypeMap: Record<string, Array<{ value: string; label: string; icon?: string }>> = {
        movita_vec: [
          { value: 'elektronika', label: 'Elektronika a technika', icon: '💻' },
          { value: 'nabytek', label: 'Nábytek a vybavení', icon: '🪑' },
          { value: 'obleceni', label: 'Oblečení a obuv', icon: '👕' },
          { value: 'sportovni', label: 'Sportovní vybavení', icon: '⚽' },
          { value: 'nastroje', label: 'Nářadí a nástroje', icon: '🔧' },
          { value: 'ostatni', label: 'Ostatní movité věci', icon: '📦' },
        ],
        nemovitost: [
          { value: 'byt', label: 'Byt v osobním vlastnictví', icon: '🏢' },
          { value: 'dum', label: 'Rodinný dům', icon: '🏠' },
          { value: 'pozemek', label: 'Pozemek', icon: '🌳' },
          { value: 'chata', label: 'Chata/chalupa', icon: '🏡' },
          { value: 'garaz', label: 'Garáž/garážové stání', icon: '🚗' },
          { value: 'komercni', label: 'Komerční nemovitost', icon: '🏪' },
        ],
        vozidlo: [
          { value: 'auto', label: 'Osobní automobil', icon: '🚗' },
          { value: 'motorka', label: 'Motocykl', icon: '🏍️' },
          { value: 'nakladni', label: 'Nákladní vozidlo', icon: '🚚' },
          { value: 'prives', label: 'Přívěs/návěs', icon: '🚐' },
          { value: 'lod', label: 'Loď/člun', icon: '⛵' },
          { value: 'specialni', label: 'Speciální vozidlo', icon: '🚜' },
        ],
        zvirata: [
          { value: 'pes', label: 'Pes', icon: '🐕' },
          { value: 'kocka', label: 'Kočka', icon: '🐈' },
          { value: 'kun', label: 'Kůň', icon: '🐎' },
          { value: 'hospodarsky', label: 'Hospodářské zvíře', icon: '🐄' },
          { value: 'exoticke', label: 'Exotické zvíře', icon: '🦜' },
          { value: 'ostatni_zvirata', label: 'Ostatní zvířata', icon: '🐾' },
        ],
      };

      return subtypeMap[contractType] || [{ value: 'obecny', label: 'Obecný typ', icon: '📝' }];
    };

    // Update the field's options
    const newOptions = getSubtypeOptions(contractType);
    subtypeField.options = newOptions;

    // Reset contract_subtype value when contract_type changes
    const currentSubtype = watchedData['contract_subtype'];
    const isValidOption = newOptions.some(opt => opt.value === currentSubtype);
    if (!isValidOption && newOptions.length > 0) {
      // Reset to empty or first option
      setValue('contract_subtype', '');
    }
  }, [watchedData['contract_type'], config, setValue]);

  /**
   * Evaluate conditional logic for a field (without layout overrides)
   */
  const isFieldConditionSatisfied = useCallback(
    (field: FieldConfig): boolean => {
      if (!field.conditional) return true;

      const watchedValue = watchedData[field.conditional.field];
      const { operator, value, showWhenTrue = true } = field.conditional;

      let condition = false;

      switch (operator) {
        case 'equals':
          condition = watchedValue === value;
          break;
        case 'notEquals':
          condition = watchedValue !== value;
          break;
        case 'includes':
          condition = Array.isArray(watchedValue)
            ? watchedValue.includes(value)
            : String(watchedValue).includes(String(value));
          break;
        case 'notIncludes':
          condition = Array.isArray(watchedValue)
            ? !watchedValue.includes(value)
            : !String(watchedValue).includes(String(value));
          break;
        case 'greaterThan':
          condition = Number(watchedValue) > Number(value);
          break;
        case 'lessThan':
          condition = Number(watchedValue) < Number(value);
          break;
        default:
          condition = false;
      }

      return showWhenTrue ? condition : !condition;
    },
    [watchedData]
  );

  /**
   * Get field names for current step (for validation)
   */
  const getStepFields = useCallback(
    (stepIndex: number): string[] => {
      if (!steps[stepIndex]) return [];

      return steps[stepIndex].fields
        .filter(isFieldConditionSatisfied)
        .map((field) => field.name);
    },
    [steps, isFieldConditionSatisfied]
  );

  /**
   * Build validation rules from field config
   */
  const buildValidationRules = (field: FieldConfig) => {
    const rules: any = {};

    if (!field.validation) return rules;

    field.validation.forEach((rule) => {
      switch (rule.type) {
        case 'required':
          rules.required = rule.message;
          break;
        case 'minLength':
          rules.minLength = { value: rule.value as number, message: rule.message };
          break;
        case 'maxLength':
          rules.maxLength = { value: rule.value as number, message: rule.message };
          break;
        case 'min':
          rules.min = { value: rule.value as number, message: rule.message };
          break;
        case 'max':
          rules.max = { value: rule.value as number, message: rule.message };
          break;
        case 'pattern':
          rules.pattern = {
            value: rule.value instanceof RegExp ? rule.value : new RegExp(rule.value as string),
            message: rule.message,
          };
          break;
        case 'email':
          rules.pattern = {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: rule.message,
          };
          break;
        case 'custom':
          if (rule.validator) {
            rules.validate = async (value: any) => {
              const result = await rule.validator!(value, getValues());
              return result || rule.message;
            };
          }
          break;
      }
    });

    return rules;
  };

  /**
   * Handle next step
   */
  const handleNext = async () => {
    const currentStepFields = getStepFields(activeStep);
    const isValid = currentStepFields.length === 0 ? true : await trigger(currentStepFields);

    if (isValid) {
      // Run step-level validation if defined
      const stepValidation = steps[activeStep].validation;
      if (stepValidation) {
        for (const rule of stepValidation) {
          if (rule.validator) {
            const result = await rule.validator(null, getValues());
            if (!result) {
              setError(rule.message);
              return;
            }
          }
        }
      }

      if (!completedSteps.includes(activeStep)) {
        setCompletedSteps([...completedSteps, activeStep]);
      }

      if (activeStep < steps.length - 1) {
        setActiveStep(activeStep + 1);
        setError('');
      }
    }
  };

  /**
   * Handle previous step
   */
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError('');
    }
  };

  /**
   * Check if can proceed to next step
   */
  const canGoNext = (): boolean => {
    const currentStepFields = getStepFields(activeStep);

    if (currentStepFields.length === 0) return true;

    const values = getValues();
    return currentStepFields.every((fieldName) => {
      const value = values[fieldName];
      const field = steps[activeStep].fields.find((f) => f.name === fieldName);

      // Check if field is required
      const isRequired = field?.validation?.some((v) => v.type === 'required');
      if (!isRequired) return true;

      // Check if value exists
      return value !== undefined && value !== '' && value !== null;
    });
  };

  /**
   * Check if all required visible fields across all steps are completed
   */
  const areAllRequiredFieldsCompleted = (): boolean => {
    const values = getValues();
    for (let i = 0; i < steps.length; i++) {
      const requiredVisibleFields = steps[i].fields
        .filter(isFieldConditionSatisfied)
        .filter((f) => f.validation?.some((v) => v.type === 'required'))
        .map((f) => f.name);

      const ok = requiredVisibleFields.every((name) => {
        const value = (values as any)[name];
        return value !== undefined && value !== '' && value !== null;
      });
      if (!ok) return false;
    }
    return true;
  };

  /**
   * Handle form submission
   */
  const handleFormSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');

    try {
      // Run custom validation if provided
      if (config.customValidation) {
        const validationResult = config.customValidation(data);
        if (!validationResult.valid) {
          setError('Formulář obsahuje chyby. Zkontrolujte všechna pole.');
          if (validationResult.errors) {
            // Set field errors
            Object.entries(validationResult.errors).forEach(([field, message]) => {
              console.error(`Validation error for ${field}: ${message}`);
            });
          }
          return;
        }
      }

      // Run form-level validation if provided
      if (config.validation?.onSubmit) {
        const isValid = await config.validation.onSubmit(data);
        if (!isValid) {
          setError('Validace formuláře selhala.');
          return;
        }
      }

      // Transform data if transformer provided
      const transformedData = config.transformData ? config.transformData(data) : data;

      // Submit - support both new and legacy patterns
      if (onSubmit) {
        await onSubmit(transformedData);
      } else if (onSuccess) {
        // Legacy pattern: call API directly and then onSuccess callback
        // This mimics the old AppleStyle forms behavior
        onSuccess(transformedData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Odeslání formuláře selhalo';
      setError(errorMessage);
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle preview
   */
  const handlePreview = () => {
    if (onPreview) {
      onPreview(getValues());
    }
  };

  // Auto-open full preview when user reaches the last step
  const previewOpenedRef = React.useRef(false);
  useEffect(() => {
    const lastIndex = (steps.length || 1) - 1;
    if (activeStep === lastIndex && !previewOpenedRef.current) {
      previewOpenedRef.current = true;
      handlePreview();
    }
    if (activeStep !== lastIndex) {
      previewOpenedRef.current = false;
    }
  }, [activeStep, steps]);

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    if (!loading && onClose) {
      onClose();
    }
  };

  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    // Ignore backdrop clicks to prevent accidental closing during form filling
    if (reason === 'backdropClick') return;
    handleClose();
  };

  /**
   * Update incomplete steps tracker
   */
  useEffect(() => {
    const newIncompleteSteps: number[] = [];
    const values = getValues();

    for (let i = 0; i <= Math.max(activeStep, ...completedSteps); i++) {
      const stepFields = getStepFields(i);

      const isStepValid = stepFields.every((fieldName) => {
        const value = values[fieldName];
        const field = steps[i]?.fields.find((f) => f.name === fieldName);
        const isRequired = field?.validation?.some((v) => v.type === 'required');

        if (!isRequired) return true;
        return value !== undefined && value !== '' && value !== null;
      });

      if (!isStepValid && !completedSteps.includes(i) && i !== activeStep) {
        newIncompleteSteps.push(i);
      }
    }

    // Only update if changed to avoid infinite loop
    if (JSON.stringify(newIncompleteSteps) !== JSON.stringify(incompleteSteps)) {
      setIncompleteSteps(newIncompleteSteps);
    }
  }, [watchedData, activeStep, completedSteps, steps, incompleteSteps]);

  /**
   * Render live preview of the contract
   */
  const renderLivePreview = () => {
    const values = getValues();

    // Show placeholder - check if we're on first step or basic info not filled
    // For purchase contracts, check contract_type and contract_subtype
    const isBasicInfoMissing = config.type === 'purchase'
      ? !values.contract_type || !values.contract_subtype
      : activeStep === 0;

    if (isBasicInfoMissing) {
      return (
        <Card sx={{
          p: 3,
          borderRadius: '20px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 2px 2px rgba(18, 14, 13, 0.15)',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              📄 Náhled smlouvy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.type === 'purchase'
                ? 'Náhled smlouvy bude k dispozici po upřesnění typu a předmětu koupě'
                : 'Náhled smlouvy bude k dispozici po vyplnění základních informací'}
            </Typography>
            {config.type === 'purchase' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Vyberte prosím typ smlouvy v kroku 1
              </Typography>
            )}
          </Box>
        </Card>
      );
    }

    // Generate contract title based on type
    const getContractTitle = () => {
      const type = config.type;

      if (type === 'purchase') {
        const contractType = values.contract_type;
        const subtype = values.contract_subtype;

        if (contractType === 'vozidlo') {
          if (subtype === 'auto') return 'KUPNÍ SMLOUVA NA AUTOMOBIL';
          if (subtype === 'motorka') return 'KUPNÍ SMLOUVA NA MOTOCYKL';
          return 'KUPNÍ SMLOUVA NA VOZIDLO';
        } else if (contractType === 'nemovitost') {
          if (subtype === 'byt') return 'KUPNÍ SMLOUVA NA BYT';
          if (subtype === 'dum') return 'KUPNÍ SMLOUVA NA DŮM';
          if (subtype === 'pozemek') return 'KUPNÍ SMLOUVA NA POZEMEK';
          return 'KUPNÍ SMLOUVA NA NEMOVITOST';
        } else if (contractType === 'movita_vec') {
          return 'KUPNÍ SMLOUVA NA MOVITOU VĚC';
        }
        return 'KUPNÍ SMLOUVA';
      } else if (type === 'rental') {
        return 'NÁJEMNÍ SMLOUVA';
      } else if (type === 'employment') {
        return 'PRACOVNÍ SMLOUVA';
      } else if (type === 'work') {
        return 'SMLOUVA O DÍLO';
      } else if (type === 'gift') {
        return 'DAROVACÍ SMLOUVA';
      }

      return config.metadata?.title?.toUpperCase() || 'SMLOUVA';
    };

    // Helper to get contract type description
    const getContractTypeDescription = () => {
      if (config.type !== 'purchase') return config.metadata?.title || '';

      const contractType = values.contract_type;
      const subtype = values.contract_subtype;

      const typeMap: Record<string, string> = {
        'movita_vec': 'Movitá věc',
        'nemovitost': 'Nemovitost',
        'vozidlo': 'Vozidlo',
        'zvirata': 'Zvířata',
      };

      const subtypeMap: Record<string, Record<string, string>> = {
        'movita_vec': { 'elektronika': 'Elektronika', 'nabytek': 'Nábytek', 'obleceni': 'Oblečení' },
        'nemovitost': { 'byt': 'Byt', 'dum': 'Rodinný dům', 'pozemek': 'Pozemek' },
        'vozidlo': { 'auto': 'Automobil', 'motorka': 'Motocykl' },
      };

      const typeStr = typeMap[contractType] || contractType;
      const subtypeStr = subtypeMap[contractType]?.[subtype] || subtype;

      return `${typeStr} - ${subtypeStr}`;
    };

    // Render live preview with contract sections
    return (
      <Card sx={{
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        background: 'white'
      }}>
        <Box sx={{
          p: 2,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: '#1F1F1F'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
            📄 Náhled smlouvy
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {config.type === 'purchase' && values.contract_type
              ? `Typ: ${getContractTypeDescription()}`
              : 'Aktualizuje se při vyplňování'}
          </Typography>
        </Box>

        <Box sx={{ p: 3, fontSize: '0.85rem', lineHeight: 1.6 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
            {getContractTitle()}
          </Typography>

          {/* Legal reference */}
          <Typography paragraph sx={{ fontSize: '0.8rem', color: '#6c757d' }}>
            podle § 2079 a násl. zákona č. 89/2012 Sb., občanský zákoník
            {config.type === 'purchase' && values.contract_type === 'nemovitost' && ', ve spojení s katastrálním zákonem'}
            {config.type === 'purchase' && values.contract_type === 'vozidlo' && ', ve spojení se zákonem o podmínkách provozu vozidel na pozemních komunikacích'}
          </Typography>

          {/* Contract type specific sections */}
          {config.type === 'purchase' && (
            <>
              {/* Seller section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Prodávající:
              </Typography>
              <Typography paragraph sx={{ pl: 2, color: values.seller_name ? 'inherit' : '#adb5bd' }}>
                {values.seller_name || '[Jméno prodávajícího]'}<br/>
                {values.seller_address || '[Adresa prodávajícího]'}<br/>
                {values.seller_id && `R.č./IČO: ${values.seller_id}`}
                {values.seller_email && <><br/>E-mail: {values.seller_email}</>}
              </Typography>

              {/* Buyer section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Kupující:
              </Typography>
              <Typography paragraph sx={{ pl: 2, color: values.buyer_name ? 'inherit' : '#adb5bd' }}>
                {values.buyer_name || '[Jméno kupujícího]'}<br/>
                {values.buyer_address || '[Adresa kupujícího]'}<br/>
                {values.buyer_id && `R.č./IČO: ${values.buyer_id}`}
                {values.buyer_email && <><br/>E-mail: {values.buyer_email}</>}
              </Typography>

              {/* Item description */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Předmět koupě:
              </Typography>
              <Box sx={{ pl: 2, color: values.item_description ? 'inherit' : '#adb5bd', mb: 2 }}>
                <Typography component="div" paragraph>
                  {values.item_description || `[Popis ${values.contract_type === 'vozidlo' ? 'vozidla' : values.contract_type === 'nemovitost' ? 'nemovitosti' : 'předmětu koupě'}]`}
                </Typography>
                {values.item_category && <Typography>Kategorie: {values.item_category}</Typography>}
                {values.item_condition && <Typography>Stav: {values.item_condition}</Typography>}
                {/* Identification label depends on animal context (type, subtype, or description) */}
                {(
                  values.contract_type === 'zvirata' ||
                  ['pes', 'kocka', 'kun', 'hospodarsky', 'exoticke', 'ostatni_zvirata'].includes((values as any).contract_subtype) ||
                  /(kočk|kock|pes|psa|psí|kůň|kun|zvíř)/i.test(values.item_description || '')
                ) ? (
                  values.animal_registration_number && (
                    <Typography>Evidenční číslo: {values.animal_registration_number}</Typography>
                  )
                ) : (
                  values.serial_number && (
                    <Typography>Sériové číslo: {values.serial_number}</Typography>
                  )
                )}
                {values.warranty_info && <Typography>Záruka: {values.warranty_info}</Typography>}
              </Box>

              {/* Price */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Kupní cena a platební podmínky:
              </Typography>
              <Typography paragraph sx={{ pl: 2, color: values.price ? 'inherit' : '#adb5bd' }}>
                Kupní cena: <strong>{values.price ? `${values.price.toLocaleString()} Kč` : '[Kupní cena]'}</strong><br/>
                Způsob platby: {values.payment_method || '[Způsob platby]'}
                {values.payment_terms && <><br/>{values.payment_terms}</>}
              </Typography>

              {/* Delivery */}
              {(values.delivery_date || values.delivery_location) && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Předání:
                  </Typography>
                  <Typography paragraph sx={{ pl: 2 }}>
                    {values.delivery_date && <>Datum předání: {new Date(values.delivery_date).toLocaleDateString('cs-CZ')}<br/></>}
                    {values.delivery_location && <>Místo předání: {values.delivery_location}</>}
                  </Typography>
                </>
              )}

              {/* Legal clauses */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Závěrečná ustanovení:
                </Typography>
                <Typography paragraph sx={{ pl: 2, fontSize: '0.85rem' }}>
                  • Kupující prohlašuje, že si předmět koupě řádně prohlédl a je mu znám jeho stav
                  <br/>• Vlastnické právo přechází na kupujícího okamžikem zaplacení kupní ceny
                  <br/>• Nebezpečí škody přechází na kupujícího převzetím předmětu koupě
                  <br/>• Tato smlouva je vyhotovena ve dvou stejnopisech
                  <br/>• Smlouva nabývá platnosti a účinnosti dnem podpisu oběma stranami
                </Typography>
              </Box>
            </>
          )}

          {config.type === 'rental' && (
            <>
              {/* Landlord section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Pronajímatel:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.landlord_name || '[Jméno pronajímatele]'}<br/>
                {values.landlord_address || '[Adresa]'}<br/>
                {values.landlord_id && `R.č./IČO: ${values.landlord_id}`}
              </Typography>

              {/* Tenant section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Nájemce:
              </Typography>
              {/* Primary tenant */}
              <Typography paragraph sx={{ pl: 2 }}>
                {values.tenant_name || '[Jméno nájemce]'}<br/>
                {values.tenant_address || '[Adresa]'}<br/>
                {values.tenant_id && `R.č./IČO: ${values.tenant_id}`}
              </Typography>
              {/* Additional tenants if provided */}
              {Array.from({ length: 5 }).map((_, idx) => {
                const i = idx + 2;
                const nameKey = `tenant_name_${i}`;
                const addrKey = `tenant_address_${i}`;
                const idKey = `tenant_id_${i}`;
                const hasAny = (values as any)[nameKey] || (values as any)[addrKey] || (values as any)[idKey];
                if (!hasAny) return null;
                return (
                  <Typography key={i} paragraph sx={{ pl: 2 }}>
                    {(values as any)[nameKey] || `[Jméno nájemce ${i}]`}<br/>
                    {(values as any)[addrKey] || '[Adresa]'}<br/>
                    {(values as any)[idKey] && `R.č./IČO: ${(values as any)[idKey]}`}
                  </Typography>
                );
              })}

              {/* Property */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Předmět nájmu:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.property_description || '[Popis nemovitosti]'}<br/>
                Adresa: {values.property_address || '[Adresa nemovitosti]'}
              </Typography>

              {/* Rent */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Výše nájemného:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                Měsíční nájemné: <strong>{values.rent_amount ? `${values.rent_amount.toLocaleString()} Kč` : '[Částka]'}</strong><br/>
                Kauce: {values.deposit_amount ? `${values.deposit_amount.toLocaleString()} Kč` : '[Částka]'}
              </Typography>
            </>
          )}

          {config.type === 'employment' && (
            <>
              {/* Employer section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Zaměstnavatel:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.employer_name || '[Název zaměstnavatele]'}<br/>
                {values.employer_address || '[Adresa]'}<br/>
                {(values.employer_ico || values.employer_id) && `IČO: ${values.employer_ico || values.employer_id}`}
              </Typography>

              {/* Employee section */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Zaměstnanec:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.employee_name || '[Jméno zaměstnance]'}<br/>
                {values.employee_address || '[Adresa]'}<br/>
                {values.employee_id && `R.č.: ${values.employee_id}`}
              </Typography>

              {/* Position */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Pracovní pozice:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values.position_title || values.position) || '[Název pozice]'}<br/>
                Místo výkonu práce: {(values.workplace_address || values.workplace) || '[Místo]'}
              </Typography>

              {/* Salary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Mzda:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                <strong>{(values.salary_amount || values.salary) ? `${(values.salary_amount || values.salary).toLocaleString()} Kč` : '[Částka]'}</strong> / {values.salary_period || 'měsíc'}
              </Typography>
            </>
          )}

          {config.type === 'work' && (
            <>
              {/* Contractor (Zhotovitel) */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Zhotovitel:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.contractor_name || '[Jméno/Název zhotovitele]'}<br/>
                {values.contractor_address || '[Adresa]'}<br/>
                {values.contractor_id && `R.č./IČO: ${values.contractor_id}`}
                {values.contractor_email && <><br/>E‑mail: {values.contractor_email}</>}
              </Typography>

              {/* Client (Objednavatel) */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Objednavatel:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.client_name || '[Jméno/Název objednavatele]'}<br/>
                {values.client_address || '[Adresa]'}<br/>
                {values.client_id && `R.č./IČO: ${values.client_id}`}
                {values.client_email && <><br/>E‑mail: {values.client_email}</>}
              </Typography>

              {/* Work details */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Předmět díla:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values as any).work_description || '[Popis díla]'}
                {(values as any).work_location && <><br/>Místo provedení: {(values as any).work_location}</>}
                {(values as any).materials_provider && <><br/>Materiál dodá: {(values as any).materials_provider}</>}
                {(values as any).work_specifications && <><br/>Specifikace: {(values as any).work_specifications}</>}
              </Typography>

              {/* Price & Payment */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Cena a platby:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                Cena díla: <strong>{(values as any).total_price ? `${Number((values as any).total_price).toLocaleString()} Kč` : '[Částka]'}</strong><br/>
                Způsob platby: {(values as any).payment_method || '[Způsob platby]'}
                {(values as any).payment_schedule && <><br/>{(values as any).payment_schedule}</>}
              </Typography>

              {/* Terms & Warranties */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Termíny a záruky:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values as any).completion_date && <>Termín dokončení: {new Date((values as any).completion_date).toLocaleDateString('cs-CZ')}<br/></>}
                {(values as any).inspection_period && <>Lhůta pro kontrolu: {(values as any).inspection_period} dní<br/></>}
                {(values as any).warranty_period && <>Záruční doba: {(values as any).warranty_period} měsíců<br/></>}
                {(values as any).penalty_clause && <>Smluvní pokuty: {(values as any).penalty_clause}<br/></>}
                {(values as any).insurance_required && <>Pojištění odpovědnosti požadováno<br/></>}
                {(values as any).special_conditions && <>Zvláštní podmínky: {(values as any).special_conditions}</>}
              </Typography>
            </>
          )}

          {config.type === 'gift' && (
            <>
              {/* Donor */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Dárce:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values as any).donor_name || '[Jméno/Název dárce]'}<br/>
                {(values as any).donor_address || '[Adresa]'}<br/>
                {(values as any).donor_id && `R.č./IČO: ${(values as any).donor_id}`}
                {(values as any).donor_email && <><br/>E‑mail: {(values as any).donor_email}</>}
              </Typography>

              {/* Recipient */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Obdarovaný:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values as any).recipient_name || '[Jméno/Název obdarovaného]'}<br/>
                {(values as any).recipient_address || '[Adresa]'}<br/>
                {(values as any).recipient_id && `R.č./IČO: ${(values as any).recipient_id}`}
                {(values as any).recipient_email && <><br/>E‑mail: {(values as any).recipient_email}</>}
              </Typography>

              {/* Gift details */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Předmět daru:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values as any).gift_description || '[Popis daru]'}
                {(values as any).gift_category && <><br/>Kategorie: {(values as any).gift_category}</>}
                {(values as any).gift_value && <><br/>Hodnota: {Number((values as any).gift_value).toLocaleString()} Kč</>}
                {(values as any).real_estate_description && <><br/>Popis nemovitosti: {(values as any).real_estate_description}</>}
                {(values as any).cadastral_info && <><br/>Katastrální údaje: {(values as any).cadastral_info}</>}
                {(values as any).encumbrances && <><br/>Břemena a omezení: {(values as any).encumbrances}</>}
              </Typography>

              {/* Transfer & formalities */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Předání a formality:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {(values as any).transfer_date && <>Datum předání: {new Date((values as any).transfer_date).toLocaleDateString('cs-CZ')}<br/></>}
                {(values as any).notary_required && <>Notářské ověření podpisů: vyžadováno<br/></>}
                {(values as any).tax_obligations && <>Daňové povinnosti: {(values as any).tax_obligations}<br/></>}
                {(values as any).gift_reason && <>Důvod darování: {(values as any).gift_reason}<br/></>}
                {(values as any).special_conditions && <>Zvláštní ujednání: {(values as any).special_conditions}</>}
              </Typography>
            </>
          )}

          {/* Special conditions for all contract types */}
          {values.special_conditions && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                Zvláštní podmínky:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.special_conditions}
              </Typography>
            </>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
              {config.type === 'purchase' && values.contract_type && values.contract_subtype
                ? `Smlouva typu "${getContractTypeDescription()}" bude dokončena po vyplnění všech povinných údajů...`
                : 'Smlouva bude dokončena po vyplnění všech povinných údajů...'}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  /**
   * Render form content
   */
  const renderFormContent = () => (
    <>
      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 3,
              border: '1px solid rgba(211, 47, 47, 0.2)',
              bgcolor: 'rgba(211, 47, 47, 0.05)',
            }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        </Fade>
      )}

      <StepperProgress
        steps={steps.map((step) => step.title)}
        activeStep={activeStep}
        completedSteps={completedSteps}
        incompleteSteps={incompleteSteps}
        color="success"
        onStepClick={(stepIndex) => {
          if (stepIndex === activeStep) return;

          // Allow going back to any completed step
          if (completedSteps.includes(stepIndex)) {
            setActiveStep(stepIndex);
            return;
          }

          // Allow going forward by exactly one step when current step is valid
          if (stepIndex === activeStep + 1 && canGoNext()) {
            if (!completedSteps.includes(activeStep)) {
              setCompletedSteps([...completedSteps, activeStep]);
            }
            setActiveStep(stepIndex);
          }
        }}
      />

      {/* 2-column layout: Form | Live Preview - Mobile optimized */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 3fr' },
        gap: { xs: 2, sm: 3, lg: 4 },
        borderRadius: '0 !important',
        '& *': {
          borderRadius: '0 !important'
        }
      }}>
        {/* LEFT COLUMN: Form fields */}
        <Box sx={{
          borderRadius: '0 !important',
          '& *': {
            borderRadius: '0 !important'
          }
        }}>
          {steps.map((step, stepIndex) => (
            <FormStep
              key={stepIndex}
              title={step.title}
              subtitle={step.subtitle}
              icon={
                (() => {
                  const name = (step.icon || '').toString().toLowerCase();
                  let emoji = '📝';
                  if (name.includes('business')) emoji = '🏢';
                  else if (name.includes('person')) emoji = '🧑';
                  else if (name.includes('work')) emoji = '💼';
                  else if (name.includes('home')) emoji = '🏠';
                  else if (name.includes('build')) emoji = '🛠️';
                  else if (name.includes('gift')) emoji = '🎁';
                  else if (name.includes('description')) emoji = '📝';
                  else if (name.includes('payment')) emoji = '💳';
                  else if (name.includes('shoppingcart') || name.includes('shopping') || name.includes('cart')) emoji = '🛒';
                  else if (name.includes('check')) emoji = '✅';
                  else if (name.includes('warning')) emoji = '⚠️';
                  return <span aria-hidden="true" style={{ fontSize: 28, lineHeight: 1 }}>{emoji}</span>;
                })()
              }
              color={(step.color || 'primary') as 'primary' | 'success' | 'warning' | 'error'}
              show={stepIndex === activeStep}
              headerActions={undefined}
            >
              {/* Rental: Add person (+) button for tenants on step "Kdo si pronajme?" */}
              {config.type === 'rental' && stepIndex === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip title="Přidat dalšího pronajímatele do smlouvy">
                    <span>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setExtraLandlords((c) => Math.min(5, c + 1))}
                        aria-label="Přidat dalšího pronajímatele do smlouvy"
                        data-testid="add-landlord-button"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}

              {config.type === 'rental' && stepIndex === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<AddIcon />}
                    onClick={() => setExtraLandlords((c) => Math.min(5, c + 1))}
                    aria-label="Přidat dalšího pronajímatele do smlouvy"
                    data-testid="add-landlord-text-button"
                  >
                    Přidat pronajímatele
                  </Button>
                  {extraLandlords > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setExtraLandlords((c) => Math.max(0, c - 1))}
                    >
                      Odebrat posledního
                    </Button>
                  )}
                </Box>
              )}

              {config.type === 'rental' && stepIndex === 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip title="Přidat dalšího nájemníka do smlouvy">
                    <span>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setExtraTenants((c) => Math.min(5, c + 1))}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}

              {config.type === 'rental' && stepIndex === 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<AddIcon />}
                    onClick={() => setExtraTenants((c) => Math.min(5, c + 1))}
                    aria-label="Přidat dalšího nájemníka do smlouvy"
                    data-testid="add-tenant-button"
                  >
                    Přidat nájemníka
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: { xs: 2, sm: 3 } }}>
                {/* Custom grouped rendering for rental landlords (step "Kdo pronajímá?") */}
                {config.type === 'rental' && stepIndex === 0 && (
                  <>
                    {/* Landlord 1 group */}
                    <Box sx={{ gridColumn: 'span 12' }}>
                      <Box sx={{
                        p: 2,
                        border: '1px solid rgba(0,0,0,0.12)',
                        borderRadius: 2,
                        backgroundColor: '#FFFFFF',
                      }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                          Pronajímatel 1
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: { xs: 2, sm: 3 } }}>
                          {['landlord_name','landlord_address','landlord_id','landlord_email','landlord_business_id'].map((fname) => {
                            const baseField = step.fields.find(f => f.name === fname);
                            if (!baseField) return null;
                            const effectiveField: FieldConfig = baseField.name.includes('email')
                              ? { ...baseField, gridProps: { xs: 12, md: 12 } as any }
                              : baseField;
                            return (
                              <Controller
                                key={effectiveField.name}
                                name={effectiveField.name}
                                control={control}
                                rules={buildValidationRules(effectiveField)}
                                render={({ field: fieldProps, fieldState }) => (
                                  <FieldRenderer
                                    field={effectiveField}
                                    fieldProps={fieldProps}
                                    fieldState={fieldState}
                                    suggestions={ragSuggestions[effectiveField.name]}
                                    ragLoading={ragLoading && effectiveField.ragEnabled}
                                  />
                                )}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>

                    {/* Additional landlords */}
                    {Array.from({ length: extraLandlords }).map((_, idx) => {
                      const i = idx + 2;
                      const clones = [
                        { base: 'landlord_name', label: `Jméno a příjmení / Název firmy (Pronajímatel ${i})`, grid: { xs: 12 } },
                        { base: 'landlord_address', label: `Adresa (Pronajímatel ${i})`, grid: { xs: 12 } },
                        { base: 'landlord_id', label: `Rodné číslo / IČO (Pronajímatel ${i})`, grid: { xs: 12, md: 6 } },
                        { base: 'landlord_email', label: `E-mail (volitelné) (Pronajímatel ${i})`, grid: { xs: 12 } },
                        { base: 'landlord_business_id', label: `DIČ (volitelné) (Pronajímatel ${i})`, grid: { xs: 12, md: 6 } },
                      ];
                      return (
                        <Box key={`landlord-box-${i}`} sx={{ gridColumn: 'span 12' }}>
                          <Box sx={{
                            p: 2,
                            mt: 1,
                            border: '1px dashed rgba(231, 47, 13, 0.4)',
                            borderRadius: 2,
                            backgroundColor: '#FFF9F9',
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#e72f0d' }}>
                              Pronajímatel {i} (další)
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: { xs: 2, sm: 3 } }}>
                              {clones.map((c) => {
                                const name = `${c.base}_${i}`;
                                const baseField = step.fields.find(f => f.name === c.base);
                                if (!baseField) return null;
                                const fieldCfg: FieldConfig = {
                                  ...baseField,
                                  name,
                                  label: c.label,
                                  validation: baseField.validation?.filter(v => v.type !== 'required'),
                                  gridProps: c.grid as any,
                                };
                                return (
                                  <Controller
                                    key={fieldCfg.name}
                                    name={fieldCfg.name}
                                    control={control}
                                    rules={buildValidationRules(fieldCfg)}
                                    render={({ field: fieldProps, fieldState }) => (
                                      <FieldRenderer
                                        field={fieldCfg}
                                        fieldProps={fieldProps}
                                        fieldState={fieldState}
                                        suggestions={ragSuggestions[fieldCfg.name]}
                                        ragLoading={false}
                                      />
                                    )}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </>
                )}
                {/* Default field rendering except for rental steps 0 and 1 */}
                {!(config.type === 'rental' && (stepIndex === 0 || stepIndex === 1)) && step.fields.map((field) => (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={buildValidationRules(field)}
                    render={({ field: fieldProps, fieldState }) => {
                      const conditionMet = isFieldConditionSatisfied(field);
                      const checkboxField = isCheckboxField(field);

                      if (!conditionMet && !checkboxField) {
                        // For non-checkbox fields we keep component mounted but hidden to preserve state
                        return (
                          <Box sx={{ display: 'none' }}>
                            <FieldRenderer
                              field={field}
                              fieldProps={fieldProps}
                              fieldState={fieldState}
                              suggestions={ragSuggestions[field.name]}
                              ragLoading={ragLoading && field.ragEnabled}
                            />
                          </Box>
                        );
                      }

                      const effectiveField = checkboxField && !conditionMet
                        ? {
                            ...field,
                            disabled: true,
                            helperText: field.helperText || 'Tento přepínač není pro aktuální nastavení relevantní. Pokud jej chcete vypnout, stačí jej odškrtnout.',
                          }
                        : field;

                      return (
                        <FieldRenderer
                          field={effectiveField}
                          fieldProps={fieldProps}
                          fieldState={fieldState}
                          suggestions={ragSuggestions[field.name]}
                          ragLoading={ragLoading && field.ragEnabled}
                        />
                      );
                    }}
                  />
                ))}

                {/* Custom grouped rendering for rental tenants (step "Kdo si pronajme?") */}
                {config.type === 'rental' && stepIndex === 1 && (
                  <>
                    {/* Tenant 1 group */}
                    <Box sx={{ gridColumn: 'span 12' }}>
                      <Box sx={{
                        p: 2,
                        border: '1px solid rgba(0,0,0,0.12)',
                        borderRadius: 2,
                        backgroundColor: '#FFFFFF',
                      }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                          Nájemník 1
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: { xs: 2, sm: 3 } }}>
                          {['tenant_name','tenant_address','tenant_id','tenant_email'].map((fname) => {
                            const baseField = step.fields.find(f => f.name === fname);
                            if (!baseField) return null;
                            const effectiveField: FieldConfig = baseField.name.includes('email')
                              ? { ...baseField, gridProps: { xs: 12, md: 12 } as any }
                              : baseField;
                            return (
                              <Controller
                                key={effectiveField.name}
                                name={effectiveField.name}
                                control={control}
                                rules={buildValidationRules(effectiveField)}
                                render={({ field: fieldProps, fieldState }) => (
                                  <FieldRenderer
                                    field={effectiveField}
                                    fieldProps={fieldProps}
                                    fieldState={fieldState}
                                    suggestions={ragSuggestions[effectiveField.name]}
                                    ragLoading={ragLoading && effectiveField.ragEnabled}
                                  />
                                )}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>

                    {/* Additional tenants */}
                    {Array.from({ length: extraTenants }).map((_, idx) => {
                      const i = idx + 2;
                      const clones = [
                        { base: 'tenant_name', label: `Jméno a příjmení (Nájemník ${i})`, grid: { xs: 12 } },
                        { base: 'tenant_address', label: `Současná adresa (Nájemník ${i})`, grid: { xs: 12 } },
                        { base: 'tenant_id', label: `Rodné číslo (Nájemník ${i})`, grid: { xs: 12, md: 6 } },
                        { base: 'tenant_email', label: `E-mail (volitelné) (Nájemník ${i})`, grid: { xs: 12 } },
                      ];
                      return (
                        <Box key={`tenant-box-${i}`} sx={{ gridColumn: 'span 12' }}>
                          <Box sx={{
                            p: 2,
                            mt: 1,
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: 2,
                            backgroundColor: '#FFFFFF',
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                              Nájemník {i}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: { xs: 2, sm: 3 } }}>
                              {clones.map((c) => {
                                const name = `${c.base}_${i}`;
                                const baseField = step.fields.find(f => f.name === c.base);
                                if (!baseField) return null;
                                const fieldCfg: FieldConfig = {
                                  ...baseField,
                                  name,
                                  label: c.label,
                                  validation: baseField.validation?.filter(v => v.type !== 'required'),
                                  gridProps: c.grid as any,
                                };
                                return (
                                  <Controller
                                    key={fieldCfg.name}
                                    name={fieldCfg.name}
                                    control={control}
                                    rules={buildValidationRules(fieldCfg)}
                                    render={({ field: fieldProps, fieldState }) => (
                                      <FieldRenderer
                                        field={fieldCfg}
                                        fieldProps={fieldProps}
                                        fieldState={fieldState}
                                        suggestions={ragSuggestions[fieldCfg.name]}
                                        ragLoading={false}
                                      />
                                    )}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>

              {/* Rental step: explicit footer buttons to add/remove landlords/tenants */}
              {config.type === 'rental' && stepIndex === 0 && extraLandlords > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<RemoveIcon />}
                    onClick={() => setExtraLandlords((c) => Math.max(0, c - 1))}
                  >
                    Odebrat posledního
                  </Button>
                </Box>
              )}

              {config.type === 'rental' && stepIndex === 1 && extraTenants > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<RemoveIcon />}
                    onClick={() => setExtraTenants((c) => Math.max(0, c - 1))}
                  >
                    Odebrat posledního
                  </Button>
                </Box>
              )}

              {/* Review step: no manual preview button – preview opens automatically */}
            </FormStep>
          ))}
        </Box>

        {/* RIGHT COLUMN: Live preview with scrolling */}
        <Box sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'sticky',
          top: 20,
          maxHeight: '60vh',
          overflow: 'auto',
          borderRadius: '0 !important',
          '& *': {
            borderRadius: '0 !important'
          }
        }}>
          {renderLivePreview()}
        </Box>
      </Box>

      <Box sx={{
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        mt: 2,
        pt: 2,
        pb: 2,
        borderTop: '1px solid rgba(0,0,0,0.08)'
      }}>
        <StepNavigation
          currentStep={activeStep}
          totalSteps={steps.length}
          onNext={activeStep === steps.length - 1 ? rhfHandleSubmit(handleFormSubmit) : handleNext}
          onBack={handleBack}
          canGoNext={activeStep === steps.length - 1 ? areAllRequiredFieldsCompleted() : canGoNext()}
          canGoBack={activeStep > 0}
          isLoading={loading}
          autoSave={!!onAutoSave}
          autoSaveStatus={autoSaveStatus}
          color="success"
          showNext={true}
        />
      </Box>
    </>
  );

  // Render in dialog mode
  if (isDialog) {
    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'stretch',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 2px 2px rgba(18, 14, 13, 0.15)',
            minHeight: '98vh',
            height: '98vh',
            width: '98vw',
            maxWidth: '1600px',
            maxHeight: '98vh',
            m: { xs: 1, sm: 2 },
            overflow: 'visible',
          },
        }}
      >
        <DialogContent sx={{ p: 4, overflow: 'auto', position: 'relative', maxHeight: 'calc(98vh - 64px)' }}>
          <IconButton
            aria-label="Zavřít formulář"
            onClick={handleClose}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'grey.600' }}
          >
            <CloseIcon />
          </IconButton>
          {renderFormContent()}
        </DialogContent>
      </Dialog>
    );
  }

  // Render standalone
  return (
    <Box sx={{ p: 3 }}>
      {renderFormContent()}
    </Box>
  );
};

export default GenericContractForm;
